import { useEffect, type ReactNode } from 'react';
import { Tooltip, type TooltipVariant } from '../../components/Tooltip';

/**
 * ActionConfirmModal — context-aware confirmation dialog for the staff
 * action zone (Phase 5d-1, Decision 5-28).
 *
 * Mirrors the Manage Account Reference v5 §6.5 modal pattern adopted from
 * nexa-demo: non-dismissible backdrop (clicking outside has no effect — the
 * standard prevents accidental destructive transitions), top-right close
 * affordance, single primary action button, secondary Cancel.
 *
 * The "fresh-auth re-prompt" requirement from ADR-012 is enforced server-
 * side: each action handler (gateMutation) returns 401 stale_auth when the
 * JWT is past the freshness ceiling (24h since last full auth — ADR-063,
 * supersedes ADR-009's 60-min rule). DELIVERED by ADR-063: the in-place MFA
 * (TOTP) step-up re-prompt that was queued for Phase 5d-2 now lives in
 * `lib/reauth.tsx` (ReAuthProvider). Callers route mutating actions through
 * `useReAuth().withReauth(...)`, which proactively gates on a stale session,
 * surfaces the MFA re-auth modal, and resumes the in-progress action on
 * success — no full logout, no page reload, no "refresh the page" guidance.
 */
export interface ActionConfirmModalProps {
  open: boolean;
  title: string;
  intent: 'primary' | 'danger' | 'warning';
  primaryLabel: string;
  primaryDisabled?: boolean;
  loading?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  children: ReactNode;
  /** Optional extra button rendered in the action row between Cancel and
   *  the primary action. Used by DeleteBriefModal to keep Hard delete
   *  aligned with Cancel + Soft delete in a single row. */
  extraAction?: ReactNode;
  /** Optional tooltip wrapping the primary button. Useful when the
   *  consequences of the primary action deserve a designed reveal on
   *  hover (e.g. Soft delete's recoverability). */
  primaryTooltip?: { title?: string; content: ReactNode; variant?: TooltipVariant; aria?: string };
  /** Hide the top-right [×] close affordance. Customers-page modals omit it
   *  per the 5d-3b D13 no-close pattern; dismissal is via Cancel only. */
  hideClose?: boolean;
  /** Hide the Cancel button — for a statement the operator ACKNOWLEDGES rather than accepts or
   *  declines (ADR-124 A3: the sign-in modal stating the session's zone). */
  hideCancel?: boolean;
  /** Override the primary button's class. `intent="primary"` defaults to `portal-btn` (6 October
   *  2026 — it used to carry NO class, so six dialogs fell to the browser's bare grey ~13px button;
   *  #534 fixed only the sign-in zone statement by passing this prop). */
  primaryClassName?: string;
}

export function ActionConfirmModal({
  open,
  title,
  intent,
  primaryLabel,
  primaryDisabled,
  loading,
  error,
  onConfirm,
  onCancel,
  children,
  extraAction,
  primaryTooltip,
  hideClose,
  hideCancel,
  primaryClassName,
}: ActionConfirmModalProps) {
  // Escape key closes the modal when not loading. Backdrop click does NOT
  // close (Manage Account Ref v5 §6.5 "Modal backdrop is non-dismissible.
  // Clicking outside the modal has no effect, preventing accidental
  // lifecycle changes.")
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, loading, onCancel]);

  if (!open) return null;

  const primaryClass = primaryClassName ?? (
    intent === 'danger'
      ? 'portal-button-danger'
      : intent === 'warning'
        ? 'portal-button-warning'
        : 'portal-btn');

  return (
    <div className="portal-modal-backdrop" role="presentation">
      <div
        className="portal-modal portal-action-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="portal-action-modal-title"
      >
        {!hideClose && (
          <button
            type="button"
            className="portal-modal-close"
            onClick={onCancel}
            disabled={loading}
            aria-label="Close dialog"
          >
            ×
          </button>
        )}
        <h2 id="portal-action-modal-title">{title}</h2>
        <div className="portal-action-modal-body">{children}</div>
        {error && (
          <div className="portal-error" role="alert">
            {error}
          </div>
        )}
        <div className="portal-modal-actions">
          {!hideCancel && (
            <button
              type="button"
              className="portal-link-button-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
          )}
          {primaryTooltip ? (
            <Tooltip
              title={primaryTooltip.title}
              content={primaryTooltip.content}
              variant={primaryTooltip.variant}
              aria={primaryTooltip.aria}
            >
              <button
                type="button"
                className={primaryClass}
                onClick={onConfirm}
                disabled={loading || primaryDisabled}
              >
                {loading ? 'Working…' : primaryLabel}
              </button>
            </Tooltip>
          ) : (
            <button
              type="button"
              className={primaryClass}
              onClick={onConfirm}
              disabled={loading || primaryDisabled}
            >
              {loading ? 'Working…' : primaryLabel}
            </button>
          )}
          {/* extraAction sits LAST so callers using margin-left:auto can
             push it to the far right edge of the action row (e.g. the
             Hard delete button in DeleteBriefModal). */}
          {extraAction}
        </div>
      </div>
    </div>
  );
}
